import { Strings } from '@/constants/strings';
import type { FindRiskCategory } from '@/data/FindRisk';

const GAUGE_MAX = 26;
const CX = 100;
const CY = 95;
const R = 75;
const NEEDLE_LENGTH = 68;

function getCategoryLabel(cat: FindRiskCategory): string {
  switch (cat) {
    case 'low':
      return Strings.findRiskLow;
    case 'mild':
      return Strings.findRiskMild;
    case 'moderate':
      return Strings.findRiskModerate;
    case 'high':
      return Strings.findRiskHigh;
    case 'veryHigh':
      return Strings.findRiskVeryHigh;
  }
}

const categoryTextClasses: Record<FindRiskCategory, string> = {
  low: 'text-risk-low',
  mild: 'text-risk-moderate',
  moderate: 'text-warning',
  high: 'text-risk-high',
  veryHigh: 'text-risk-very-high',
};

function arcPath(
  cx: number,
  cy: number,
  r: number,
  startDeg: number,
  endDeg: number
): string {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const x1 = cx + r * Math.cos(toRad(startDeg));
  const y1 = cy - r * Math.sin(toRad(startDeg));
  const x2 = cx + r * Math.cos(toRad(endDeg));
  const y2 = cy - r * Math.sin(toRad(endDeg));
  const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
}

function scoreToAngle(score: number): number {
  const clamped = Math.min(Math.max(score, 0), GAUGE_MAX);
  return 180 - (clamped / GAUGE_MAX) * 180;
}

interface FindRiskGaugeProps {
  score: number;
  category: FindRiskCategory;
  compact?: boolean;
}

export function FindRiskGauge({ score, category, compact }: FindRiskGaugeProps) {
  const needleAngle = scoreToAngle(score);
  const needleRad = (needleAngle * Math.PI) / 180;
  const needleX = CX + NEEDLE_LENGTH * Math.cos(needleRad);
  const needleY = CY - NEEDLE_LENGTH * Math.sin(needleRad);

  const pathGreen = arcPath(CX, CY, R, 180, 144);
  const pathYellow = arcPath(CX, CY, R, 144, 90);
  const pathOrange = arcPath(CX, CY, R, 90, 45);
  const pathRed = arcPath(CX, CY, R, 45, 0);

  return (
    <div className={`flex flex-col items-center ${compact ? 'my-3' : 'my-6'}`}>
      <div className="relative w-full max-w-[280px]">
        <svg viewBox="0 0 200 110" className="w-full" aria-hidden>
          <defs>
            <filter id="findrisk-gauge-shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.15" />
            </filter>
          </defs>
          <path
            d={pathGreen}
            fill="none"
            stroke="#22C55E"
            strokeWidth="14"
            strokeLinecap="butt"
            filter="url(#findrisk-gauge-shadow)"
          />
          <path
            d={pathYellow}
            fill="none"
            stroke="#EAB308"
            strokeWidth="14"
            strokeLinecap="butt"
            filter="url(#findrisk-gauge-shadow)"
          />
          <path
            d={pathOrange}
            fill="none"
            stroke="#F97316"
            strokeWidth="14"
            strokeLinecap="butt"
            filter="url(#findrisk-gauge-shadow)"
          />
          <path
            d={pathRed}
            fill="none"
            stroke="#DC2626"
            strokeWidth="14"
            strokeLinecap="butt"
            filter="url(#findrisk-gauge-shadow)"
          />
          <line
            x1={CX}
            y1={CY}
            x2={needleX}
            y2={needleY}
            stroke="#0b63b8"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <circle cx={CX} cy={CY} r="8" fill="white" stroke="#D1D5DB" strokeWidth="2" />
        </svg>
      </div>
      <div className="mt-1 flex w-full max-w-[280px] justify-between px-2 text-xs text-[#5C5C5C]">
        <span>0</span>
        <span>{GAUGE_MAX}</span>
      </div>
      <p className={`mt-3 text-center text-base font-bold ${categoryTextClasses[category]}`}>
        {getCategoryLabel(category)}
      </p>
    </div>
  );
}
